/**
 * The 30 second showreel in the home page hero.
 *
 * The video is never part of the first load: its source is attached only after
 * the page has finished loading, so it cannot slow down the games list. It then
 * plays muted and looped while it is on screen and pauses when it scrolls away.
 * Visitors who prefer reduced motion or use data saver see the poster until
 * they press play. Offline, the poster (precached) stays and the controls go.
 *
 * The controls fade out while it plays and come back on hover, a tap or focus.
 * The expand button (or a click on the video, with a mouse) makes it bigger:
 * a larger player over a dimmed page on desktop, full screen on phones. Either
 * way it plays with sound; going back to the page mutes it again.
 */

import { iconSvg } from './icons';
import { expandMode, reelLabels, shouldAutoplay, type ExpandMode } from './reel-rules';

/** How long the controls stay up after the last sign of the visitor. */
const IDLE_MS = 2500;
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

interface NetworkInformationLike {
  saveData?: boolean;
}

interface OrientationLockable {
  lock(orientation: string): Promise<void>;
  unlock(): void;
}

function hasConnection(nav: Navigator): nav is Navigator & { connection: NetworkInformationLike } {
  return 'connection' in nav && typeof nav.connection === 'object' && nav.connection !== null;
}

function canLockOrientation(orientation: ScreenOrientation): orientation is ScreenOrientation & OrientationLockable {
  return 'lock' in orientation && typeof orientation.lock === 'function';
}

/** iOS Safari on iPhone can only put a video element in full screen, through its own player. */
function hasVideoFullscreen(video: HTMLVideoElement): video is HTMLVideoElement & { webkitEnterFullscreen(): void } {
  return 'webkitEnterFullscreen' in video && typeof video.webkitEnterFullscreen === 'function';
}

function fullscreenElement(): Element | null {
  if (document.fullscreenElement) return document.fullscreenElement;
  if ('webkitFullscreenElement' in document && document.webkitFullscreenElement instanceof Element) return document.webkitFullscreenElement;
  return null;
}

function requestElementFullscreen(element: HTMLElement): Promise<void> {
  if (typeof element.requestFullscreen === 'function') return element.requestFullscreen({ navigationUI: 'hide' });
  if ('webkitRequestFullscreen' in element && typeof element.webkitRequestFullscreen === 'function') {
    element.webkitRequestFullscreen();
    return Promise.resolve();
  }
  return Promise.reject(new Error('Fullscreen is not available'));
}

function exitElementFullscreen(): void {
  if (!fullscreenElement()) return;
  if (typeof document.exitFullscreen === 'function') document.exitFullscreen().catch(() => {});
  else if ('webkitExitFullscreen' in document && typeof document.webkitExitFullscreen === 'function') document.webkitExitFullscreen();
}

function initReel(): void {
  const video = document.querySelector<HTMLVideoElement>('.hero-reel-video');
  const frame = video?.closest<HTMLElement>('.hero-reel-frame');
  const controls = frame?.querySelector<HTMLElement>('.hero-reel-controls');
  const playButton = controls?.querySelector<HTMLButtonElement>('[data-reel-play]');
  const soundButton = controls?.querySelector<HTMLButtonElement>('[data-reel-sound]');
  const expandButton = controls?.querySelector<HTMLButtonElement>('[data-reel-expand]');
  const src = video?.dataset.src;
  if (!video || !frame || !controls || !playButton || !soundButton || !expandButton || !src) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const autoplay = shouldAutoplay({
    reducedMotion: reducedMotion.matches,
    saveData: hasConnection(navigator) && navigator.connection.saveData === true,
  });

  // Paused by the visitor (or never started), as opposed to paused because it scrolled away.
  let heldByVisitor = !autoplay;
  let heardFromStart = false;
  let expanded = false;
  let mode: ExpandMode = 'dialog';
  let lastPointer = 'mouse';
  let idleTimer = 0;
  let dialog: HTMLDialogElement | null = null;
  let placeholder: HTMLElement | null = null;
  let closing = false;

  const currentMode = (): ExpandMode =>
    expandMode({
      touch: window.matchMedia('(pointer: coarse)').matches,
      narrow: window.matchMedia('(max-width: 960px)').matches,
      elementFullscreen: document.fullscreenEnabled === true || ('webkitFullscreenEnabled' in document && document.webkitFullscreenEnabled === true),
      videoFullscreen: hasVideoFullscreen(video),
    });

  const render = () => {
    const labels = reelLabels({ playing: !video.paused, muted: video.muted, expanded, mode: expanded ? mode : currentMode() });
    playButton.innerHTML = iconSvg(labels.playIcon);
    playButton.setAttribute('aria-label', labels.playLabel);
    soundButton.innerHTML = `${iconSvg(labels.soundIcon)}<span>${labels.soundText}</span>`;
    expandButton.innerHTML = iconSvg(labels.expandIcon);
    expandButton.setAttribute('aria-label', labels.expandLabel);
  };

  /* ---- Controls that get out of the way ---- */

  const hideControls = () => {
    window.clearTimeout(idleTimer);
    if (video.paused || controls.contains(document.activeElement)) return;
    frame.dataset.idle = '';
  };
  const showControls = () => {
    delete frame.dataset.idle;
    window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(hideControls, IDLE_MS);
  };

  frame.addEventListener('pointerdown', (event) => {
    lastPointer = event.pointerType;
  });
  frame.addEventListener('pointermove', (event) => {
    if (event.pointerType === 'mouse') showControls();
  });
  frame.addEventListener('pointerleave', (event) => {
    if (event.pointerType === 'mouse') hideControls();
  });
  frame.addEventListener('focusin', showControls);
  frame.addEventListener('focusout', showControls);

  /* ---- Playback ---- */

  const play = () => {
    if (!video.getAttribute('src')) {
      video.preload = 'auto';
      video.src = src;
    }
    // A browser may still refuse (iOS in low power mode); the play button stays up.
    video.play().catch(render);
  };

  const togglePlay = () => {
    if (video.paused) {
      heldByVisitor = false;
      play();
    } else {
      heldByVisitor = true;
      video.pause();
    }
  };

  /** Sound on; the first time, from the top so the music lands with the picture. */
  const playWithSound = () => {
    video.muted = false;
    if (!heardFromStart) {
      heardFromStart = true;
      video.currentTime = 0;
    }
    heldByVisitor = false;
    play();
  };

  /* ---- Bigger view ---- */

  const animateFrame = (from: DOMRect, to: DOMRect, duration: number): Animation | null => {
    if (reducedMotion.matches || !from.width || !to.width) return null;
    const move = `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width})`;
    return frame.animate(
      [{ transform: move, transformOrigin: 'top left' }, { transform: 'none', transformOrigin: 'top left' }],
      { duration, easing: EASE },
    );
  };

  const closeDialog = () => {
    if (!dialog || !placeholder || closing) return;
    closing = true;
    const openDialogElement = dialog, slot = placeholder;
    video.muted = true;
    openDialogElement.classList.add('is-closing');
    const from = frame.getBoundingClientRect(), to = slot.getBoundingClientRect();
    const animation = reducedMotion.matches
      ? null
      : frame.animate(
          [
            { transform: 'none', transformOrigin: 'top left' },
            { transform: `translate(${to.left - from.left}px, ${to.top - from.top}px) scale(${to.width / from.width})`, transformOrigin: 'top left' },
          ],
          { duration: 320, easing: EASE, fill: 'forwards' },
        );
    const finish = () => {
      slot.replaceWith(frame);
      animation?.cancel();
      openDialogElement.close();
      openDialogElement.remove();
      dialog = null;
      placeholder = null;
      expanded = false;
      closing = false;
      render();
      expandButton.focus({ preventScroll: true });
    };
    if (animation) animation.finished.then(finish, finish);
    else finish();
  };

  const openDialog = () => {
    const from = frame.getBoundingClientRect();
    placeholder = document.createElement('div');
    placeholder.className = 'hero-reel-placeholder';
    frame.before(placeholder);

    const box = document.createElement('dialog');
    box.className = 'reel-dialog';
    box.setAttribute('aria-label', 'The freecasual.games reel');
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'hero-reel-btn icon-only reel-dialog-close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = iconSvg('x');
    box.append(close, frame);
    document.body.append(box);

    let pressedOnBackdrop = false;
    box.addEventListener('pointerdown', (event) => {
      pressedOnBackdrop = event.target === box;
    });
    box.addEventListener('click', (event) => {
      const onClose = event.target instanceof Element && event.target.closest('.reel-dialog-close') !== null;
      if (onClose || (event.target === box && pressedOnBackdrop)) closeDialog();
    });
    box.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });

    dialog = box;
    box.showModal();
    expanded = true;
    render();
    animateFrame(from, frame.getBoundingClientRect(), 420);
    close.focus({ preventScroll: true });
  };

  const expand = () => {
    if (expanded || closing) return;
    mode = currentMode();
    playWithSound();
    if (mode === 'video-fullscreen' && hasVideoFullscreen(video)) {
      video.webkitEnterFullscreen();
      return;
    }
    if (mode === 'fullscreen') {
      requestElementFullscreen(frame).then(
        () => {
          expanded = true;
          render();
          if (canLockOrientation(screen.orientation)) screen.orientation.lock('landscape').catch(() => {});
        },
        () => {
          mode = 'dialog';
          openDialog();
        },
      );
      return;
    }
    openDialog();
  };

  const collapse = () => {
    if (!expanded) return;
    if (mode === 'fullscreen') exitElementFullscreen();
    else closeDialog();
  };

  const onFullscreenChange = () => {
    if (mode !== 'fullscreen' || !expanded || fullscreenElement() === frame) return;
    expanded = false;
    video.muted = true;
    if (canLockOrientation(screen.orientation)) screen.orientation.unlock();
    render();
  };
  document.addEventListener('fullscreenchange', onFullscreenChange);
  document.addEventListener('webkitfullscreenchange', onFullscreenChange);
  // Leaving the iPhone's own player: back to muted, and keep it moving.
  video.addEventListener('webkitendfullscreen', () => {
    video.muted = true;
    if (!heldByVisitor) play();
  });

  /* ---- Wiring ---- */

  playButton.addEventListener('click', togglePlay);
  soundButton.addEventListener('click', () => {
    if (video.muted) playWithSound();
    else video.muted = true;
  });
  expandButton.addEventListener('click', () => (expanded ? collapse() : expand()));

  video.addEventListener('click', () => {
    if (frame.classList.contains('is-unavailable')) return;
    // A finger shows or hides the controls; it never opens a bigger view by surprise.
    if (lastPointer !== 'mouse') {
      if (frame.hasAttribute('data-idle')) showControls();
      else hideControls();
      return;
    }
    if (expanded) togglePlay();
    else expand();
  });

  video.addEventListener('play', () => {
    render();
    showControls();
  });
  video.addEventListener('pause', () => {
    render();
    window.clearTimeout(idleTimer);
    delete frame.dataset.idle;
  });
  video.addEventListener('volumechange', render);
  video.addEventListener('error', () => {
    controls.hidden = true;
    frame.classList.add('is-unavailable');
  });

  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting && !heldByVisitor) play();
      else if (!entry.isIntersecting && !video.paused) video.pause();
    },
    { threshold: 0.3 },
  );

  render();
  controls.hidden = false;

  const start = () => observer.observe(video);
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}

initReel();
